import { differenceInDays } from 'date-fns';
import { BrandConfig } from 'types/brand-config';

import { ListingQueryProps } from 'types/listing-query-props';
import { isValidDateParam, validateDateArray } from '@/utils/parse-safe-date';
import {
  hasExplicitGuestSearchParams,
  parseGuestCountsFromListingQuery,
  totalGuestsFromCounts,
} from '@/utils/guest-search-params';
import { resolveResortFilterId } from '@/utils/resort-search-params';

import {
  FilterNumberType,
  FilterStringArrayType,
  FilterStringType,
  ListingsFacetsInput,
  ListingsFiltersInput,
  ListingSortField,
  ListingSortInput,
  ListingsQueryInput,
  SortOrder,
} from './platform-api/__generated__/graphql';

export const DefaultListingResultSize = 24;

export const nightsBetweenDates = (arrive: string, depart: string) => {
  return differenceInDays(depart, arrive);
};

export function getListingsQueryInput(
  searchParams: ListingQueryProps,
  brand: BrandConfig
) {
  const filters: ListingsFiltersInput = {};
  if (!brand.ignoreShowOnWebsite) {
    filters.show_on_web = true;
  }
  const args: ListingsQueryInput = {
    page: {
      size: searchParams.size
        ? parseInt(searchParams.size)
        : DefaultListingResultSize,
      offset: searchParams.offset ? parseInt(searchParams.offset) : 0,
    },
  };

  // Apply brand-specific filters
  if (brand.managerIds) {
    filters.manager_ids = {
      type: FilterStringType.In,
      values: brand.managerIds,
    };
  }
  // Apply location filters
  if (searchParams.location_polygon) {
    const bounds = searchParams.location_polygon.split(';').map((section) => {
      const [lat, lon] = section.split(',').map(parseFloat);
      return { lat, lon };
    });
    bounds.length = 4;
    filters.location_polygon = {
      vertices: bounds,
    };
  }
  // Apply dates / arrivals filters (only when both arrive and depart are valid)
  if (isValidDateParam(searchParams.arrive) && isValidDateParam(searchParams.depart)) {
    const arrivals: string[] = [searchParams.arrive];
    
    if (searchParams.flex_weeks) {
      const arriveTime = new Date(searchParams.arrive).getTime();
      for (let i = 1; i <= parseInt(searchParams.flex_weeks); i++) {
        arrivals.push(
          new Date(arriveTime + i * 7 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split('T')[0]
        );
        arrivals.push(
          new Date(arriveTime - i * 7 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split('T')[0]
        );
      }
    }
    if (searchParams.flex_days) {
      const arriveTime = new Date(searchParams.arrive).getTime();
      for (let i = 1; i <= parseInt(searchParams.flex_days); i++) {
        arrivals.push(
          new Date(arriveTime + i * 24 * 60 * 60 * 1000)
            .toISOString()
            .split('T')[0]
        );
        arrivals.push(
          new Date(arriveTime - i * 24 * 60 * 60 * 1000)
            .toISOString()
            .split('T')[0]
        );
      }
    }
    
    // Validate all dates before passing to GraphQL
    const validatedArrivals = validateDateArray(arrivals);
    if (validatedArrivals.length > 0) {
      const guestCounts = parseGuestCountsFromListingQuery(searchParams);
      const totalGuests = totalGuestsFromCounts(guestCounts);
      filters.stays = {
        arrivals: validatedArrivals,
        guests: hasExplicitGuestSearchParams(searchParams) ? totalGuests : 1,
        nights: [nightsBetweenDates(searchParams.arrive, searchParams.depart)],
      };
    }
  }
  if (hasExplicitGuestSearchParams(searchParams)) {
    const totalGuests = totalGuestsFromCounts(
      parseGuestCountsFromListingQuery(searchParams)
    );
    if (totalGuests > 0) {
      filters.max_guests = {
        type: FilterNumberType.GreaterThanOrEqual,
        values: [totalGuests],
      };
    }
  }
  const resortFilterId = resolveResortFilterId(searchParams.resort_id);
  if (resortFilterId) {
    filters.resort_ids = {
      type: FilterStringType.In,
      values: [resortFilterId],
    };
  }
  if (searchParams.unit_views) {
    const unitViewParams = searchParams.unit_views.split(',');
    filters.unit_views = {
      type: FilterStringType.LikeAll,
      values: unitViewParams,
    };
  }
  if (searchParams.bathrooms) {
    filters.bathrooms = {
      type: FilterNumberType.GreaterThanOrEqual,
      values: [parseInt(searchParams.bathrooms)],
    };
  }
  if (searchParams.bedrooms) {
    const [minBedrooms, maxBedrooms] = searchParams.bedrooms.split(',');
    if (minBedrooms && !maxBedrooms) {
      filters.bedrooms = {
        type: FilterNumberType.GreaterThanOrEqual,
        values: [parseInt(minBedrooms)],
      };
    } else if (!minBedrooms && maxBedrooms) {
      filters.bedrooms = {
        type: FilterNumberType.LessThanOrEqual,
        values: [parseInt(maxBedrooms)],
      };
    } else {
      filters.bedrooms = {
        type: FilterNumberType.Between,
        values: [parseInt(minBedrooms), parseInt(maxBedrooms)],
      };
    }
  }
  if (searchParams.beds) {
    filters.beds = {
      type: FilterNumberType.GreaterThanOrEqual,
      values: [parseInt(searchParams.beds)],
    };
  }
  if (searchParams.max_guests) {
    filters.max_guests = {
      type: FilterNumberType.GreaterThanOrEqual, //? @onelittleant talk to Ned about this
      values: [parseInt(searchParams.max_guests)],
    };
  }
  if (searchParams.max_amount) {
    filters.max_amount = {
      type: FilterNumberType.LessThanOrEqual,
      values: [parseInt(searchParams.max_amount)],
    };
  }
  if (searchParams.min_amount) {
    filters.min_amount = {
      type: FilterNumberType.GreaterThanOrEqual,
      values: [parseInt(searchParams.min_amount)],
    };
  }
  if (searchParams.pets) {
    filters.max_pets = {
      type: FilterNumberType.GreaterThanOrEqual,
      values: [parseInt(searchParams.pets)],
    };
  }
  if (searchParams.max_pets) {
    filters.max_pets = {
      type: FilterNumberType.LessThanOrEqual,
      values: [parseInt(searchParams.max_pets)],
    };
    filters.pets_allowed = true;
  }
  if (searchParams.features) {
    filters.features = {
      type: FilterStringArrayType.ContainsAll,
      values: searchParams.features.split(','),
    };
  }
  if (searchParams.unit_types) {
    const unitTypesParam = searchParams.unit_types.split(',');
    filters.unit_types = {
      type: FilterStringType.LikeAll,
      values: unitTypesParam,
    };
  }
  args.filters = filters;

  if (typeof searchParams.sort_by === 'string') {
    const sorts: ListingSortInput[] = [];
    switch (searchParams.sort_by.toUpperCase()) {
      case ListingSortField.Bedrooms:
        sorts.push({
          field: ListingSortField.Bedrooms,
          order:
            searchParams.sort_dir === 'desc' ? SortOrder.Desc : SortOrder.Asc,
        });
        break;
      case ListingSortField.MaxGuests:
        sorts.push({
          field: ListingSortField.MaxGuests,
          order:
            searchParams.sort_dir === 'desc' ? SortOrder.Desc : SortOrder.Asc,
        });
        break;
      case ListingSortField.Price:
        sorts.push({
          field: ListingSortField.Price,
          order:
            searchParams.sort_dir === 'desc' ? SortOrder.Desc : SortOrder.Asc,
        });
        break;
      case ListingSortField.Title:
        sorts.push({
          field: ListingSortField.Title,
          order:
            searchParams.sort_dir === 'desc' ? SortOrder.Desc : SortOrder.Asc,
        });
        break;
      default:
        console.warn('Unknown sort field: ' + searchParams.sort_by);
        break;
    }
    if (sorts.length > 0) {
      args.sorts = sorts;
    }
  }

  // Facets
  const facets: ListingsFacetsInput = {};
  brand?.filterCategories?.forEach((filter) => {
    if (Array.isArray(filter.key)) {
      if (
        filter.key.every((key) => ['min_amount', 'max_amount'].includes(key))
      ) {
        // Price range filter has two keys in an array
        facets.price_max = true;
        facets.price_min = true;
      }
    }
    switch (filter.key) {
      case 'bathrooms':
        facets.bathrooms = true;
        break;
      case 'bedrooms':
        facets.bedrooms = true;
        break;
      case 'beds':
        facets.beds = true;
        break;
      case 'unit_views':
        facets.unit_views = true;
        break;
      case 'features':
        facets.features = true;
        break;
      case 'locales':
        facets.locales = true;
        break;
      case 'max_guests':
        facets.max_guests = true;
        break;
      case 'max_pets':
        facets.max_pets = true;
        break;
      case 'regions':
        facets.regions = true;
        break;
      case 'resorts':
        facets.resorts = true;
        break;
      case 'unit_types':
        facets.unit_types = true;
        break;
    }
  });
  if (Object.keys(facets).length > 0) {
    args.facets = facets;
  }

  return args;
}
